/**
 * Detach the staff member from one of their organizations
 * (POST /users/removeStaffOrganization). The backend refuses if the
 * member is mapped to departments or is default staff of a QR code.
 */

import { useState } from 'react';
import {
  useDataProvider,
  useGetList,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { PersonRemoveOutlined as RemoveIcon } from '@mui/icons-material';
import type { CustomDataProvider } from '@/providers/dataProvider';

export const RemoveFromOrganizationButton = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const [open, setOpen] = useState(false);
  const [organizationId, setOrganizationId] = useState('');
  const [busy, setBusy] = useState(false);

  const { data: organizations } = useGetList('organizations', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'full_name', order: 'ASC' },
  });

  if (!record) return null;

  const memberOrgIds: number[] = Array.isArray(record.organizations_array)
    ? record.organizations_array.map(Number)
    : [];
  const choices = (organizations ?? []).filter((org: any) =>
    memberOrgIds.includes(Number(org.user_id))
  );

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const result = await dataProvider.removeStaffOrganization({
        staffId: Number(record.user_id),
        organizationId: Number(organizationId),
        roleId: 3,
      });
      if (result.status === 'error') {
        notify(result.message || 'ra.notification.http_error', { type: 'error' });
      } else {
        notify('resources.staff.remove_org.success', { type: 'info' });
        setOpen(false);
        refresh();
      }
    } catch (error: any) {
      notify(error?.message || 'ra.notification.http_error', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Button
        variant="outlined"
        color="warning"
        startIcon={<RemoveIcon />}
        onClick={() => setOpen(true)}
        disabled={memberOrgIds.length === 0}
      >
        {translate('resources.staff.remove_org.button')}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{translate('resources.staff.remove_org.title')}</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            sx={{ mt: 1 }}
            label={translate('resources.staff.fields.organization_id')}
            value={organizationId}
            onChange={(event) => setOrganizationId(event.target.value)}
          >
            {choices.map((org: any) => (
              <MenuItem key={org.user_id} value={org.user_id}>
                {org.company_name || org.full_name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{translate('ra.action.cancel')}</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirm}
            disabled={!organizationId || busy}
          >
            {translate('ra.action.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RemoveFromOrganizationButton;
